import React, { useEffect, useState, useCallback } from "react";

import {
  Box,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Grid,
  useToast,
  Flex,
  Heading,
  Button,
  HStack,
  Spinner,
  Center,
  Select,
  Skeleton,
  VStack,
} from "@chakra-ui/react";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axios";

const Transaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const fetchTransactions = useCallback(async (page = 1, pageSize = 10) => {
    if (!auth.isAuthenticated) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get("/music/get/user/transactions", {
        params: {
          page: page,
          page_size: pageSize,
        },
      });
      
      // Handle the new paginated response structure
      if (response.data.transactions) {
        setTransactions(response.data.transactions);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old response structure
        const fallbackTransactions = response.data || [];
        setTransactions(fallbackTransactions);
        setPagination({
          current_page: 1,
          page_size: 10,
          total_items: fallbackTransactions.length,
          total_pages: Math.ceil(fallbackTransactions.length / 10),
          has_next: false,
          has_previous: false,
        });
      }
    } catch (error) {
      toast({
        title: "Error fetching transactions",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, navigate, toast]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handlePageChange = (newPage) => {
    fetchTransactions(newPage, pagination.page_size);
  };

  const handlePageSizeChange = (newPageSize) => {
    fetchTransactions(1, newPageSize);
  };

  // Skeleton loader component
  const TransactionSkeleton = () => (
    <VStack spacing={{ base: 2, md: 4 }} align="stretch">
      {Array(3).fill(0).map((_, index) => (
        <Box 
          key={index}
          bg="dark.500"
          borderRadius="md"
          mb={{ base: 1, md: 2 }}
        >
          <Grid
            templateColumns={{ base: "2fr 1fr", md: "3fr 1fr 1fr 0.5fr" }}
            alignItems="center"
            justifyItems="flex-start"
            w="100%"
            gap={{ base: 2, md: 4 }}
          >
            <Skeleton 
              height="20px" 
              width="200px" 
              startColor="secondary.700" 
              endColor="secondary.500" 
            />
            <Skeleton 
              height="16px" 
              width="80px"
              display={{ base: "none", md: "block" }}
              startColor="secondary.700" 
              endColor="secondary.500" 
            />
            <Skeleton 
              height="16px" 
              width="60px"
              display={{ base: "none", md: "block" }}
              startColor="secondary.700" 
              endColor="secondary.500" 
            />
            <Skeleton 
              height="16px" 
              width="16px"
              startColor="secondary.700" 
              endColor="secondary.500" 
            />
          </Grid>
        </Box>
      ))}
    </VStack>
  );

  if (loading) {
    return (
      <Box p={{ base: 2, md: 4 }} borderRadius="md" boxShadow="md" color="primary.100">
        <Skeleton height="32px" width="200px" mb={{ base: 3, md: 4 }} startColor="secondary.700" endColor="secondary.500" />
        
        {/* Pagination Controls Skeleton - Top */}
        <Flex 
          justify="space-between" 
          align="center" 
          mb={{ base: 3, md: 4 }}
          direction={{ base: "column", md: "row" }}
          gap={{ base: 2, md: 0 }}
        >
          <Skeleton height="16px" width="300px" startColor="secondary.700" endColor="secondary.500" />
          <HStack>
            <Skeleton height="16px" width="100px" startColor="secondary.700" endColor="secondary.500" />
            <Skeleton height="32px" width="60px" startColor="secondary.700" endColor="secondary.500" />
          </HStack>
        </Flex>

        <Box p={{ base: 2, md: 4 }} borderRadius="md" color="primary.100">
          {/* Header row skeleton for desktop */}
          <Skeleton 
            height="48px" 
            borderRadius="md" 
            mb={{ base: 3, md: 5 }}
            display={{ base: "none", md: "block" }}
            startColor="secondary.700" 
            endColor="secondary.500" 
          />

          <TransactionSkeleton />
        </Box>

        {/* Pagination Controls Skeleton - Bottom */}
        <Flex 
          justify="space-between" 
          align="center" 
          mt={{ base: 4, md: 6 }}
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 0 }}
        >
          <HStack>
            <Skeleton height="32px" width="80px" startColor="secondary.700" endColor="secondary.500" />
            <Skeleton height="16px" width="100px" startColor="secondary.700" endColor="secondary.500" />
            <Skeleton height="32px" width="60px" startColor="secondary.700" endColor="secondary.500" />
          </HStack>
          <Skeleton height="16px" width="150px" startColor="secondary.700" endColor="secondary.500" />
        </Flex>
      </Box>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Box p={{ base: 3, md: 4 }} borderRadius="md" boxShadow="md" color="primary.100">
        <Heading as="h3" size="lg" mb={{ base: 3, md: 4 }} color="dark.900">
          Your Purchases
        </Heading>
        <Text color="primary.100">No Transactions to show</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 2, md: 4 }} borderRadius="md" boxShadow="md" color="primary.100">
      <Heading as="h3" size="lg" mb={{ base: 3, md: 4 }} color="dark.900">
        Your Purchases
      </Heading>
      
      {/* Pagination Controls - Top */}
      <Flex 
        justify="space-between" 
        align="center" 
        mb={{ base: 3, md: 4 }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 2, md: 0 }}
      >
        <HStack>
          <Text fontSize="sm" color="primary.500">
            Showing {((pagination.current_page - 1) * pagination.page_size) + 1} - {Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of {pagination.total_items} transactions
          </Text>
        </HStack>
        
        <HStack>
          <Text fontSize="sm" color="primary.500">Items per page:</Text>
          <Select
            size="sm"
            value={pagination.page_size}
            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            w="auto"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </Select>
        </HStack>
      </Flex>

      <Box p={{ base: 2, md: 4 }} borderRadius="md" color="primary.100">
        {/* Header row for desktop */}
        <Grid
          templateColumns="3fr 1fr 1fr 0.5fr"
          gap={4}
          p={{ base: 2, md: 3 }}
          display={{ base: "none", md: "grid" }}
          bg="secondary.900"
          color="primary.100"
          borderRadius="md"
          mb={{ base: 3, md: 5 }}
        >
          <Text fontWeight="bold">Transaction ID</Text>
          <Text fontWeight="bold">Purchase Date</Text>
          <Text fontWeight="bold">Total Price</Text>
          <Text></Text>
        </Grid>

        <Accordion allowToggle>
          {transactions.map((transaction) => (
            <AccordionItem key={transaction.transaction_id} border="none">
              <h2>
                <AccordionButton
                  bg="dark.500"
                  _expanded={{
                    bg: "secondary.500",
                    color: "primary.900",
                  }}
                  _hover={{ bg: "secondary.100" }}
                  p={{ base: 3, md: 4 }}
                  borderRadius="md"
                  mb={{ base: 1, md: 2 }}
                >
                  <Grid
                    templateColumns={{ base: "2fr 1fr", md: "3fr 1fr 1fr 0.5fr" }}
                    alignItems="center"
                    justifyItems="flex-start"
                    w="100%"
                    gap={{ base: 2, md: 4 }}
                    color="primary.900"
                  >
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      gridColumn={{ base: "1/4", md: "1/2" }}
                      textAlign="left"
                      color="dark.900"
                    >
                      {transaction.transaction_id}
                    </Text>
                    <Text
                      fontSize="sm"
                      gridColumn={{ base: "1/4", md: "2/3" }}
                      textAlign="left"
                    >
                      {new Date(transaction.purchase_date).toLocaleDateString()}
                    </Text>
                    <Text
                      fontSize="sm"
                      gridColumn={{ base: "1/4", md: "3/4" }}
                      textAlign="right"
                    >
                      ${transaction.total_price.toFixed(2)}
                    </Text>
                    <AccordionIcon gridColumn={{ base: "3/4", md: "4/5" }} justifySelf="flex-end" />
                  </Grid>
                </AccordionButton>
              </h2>
              <AccordionPanel pb={{ base: 3, md: 4 }} bg="secondary.500" borderRadius="md" mb={{ base: 3, md: 5 }}>
                <Flex direction="column" color="primary.100">
                  {transaction.cart_items.items.map((item, idx) => (
                    <Box
                      key={idx}
                      mt={{ base: 1, md: 2 }}
                      borderRadius="md"
                      bg="dark.500"
                      p={{ base: 2, md: 3 }}
                    >
                      <Text as="a" href={`/track/${item.track_id}`} _hover={{ color: "primary.900" }}>
                        <Text as="span" fontSize={18} color="dark.900">Track : </Text>
                        <Text as="span" textDecoration="underline">{item.track_name}</Text>
                      </Text>
                      <Text>
                        <Text as="span" fontSize={18} color="dark.900">Quantity: </Text>{item.quantity}
                      </Text>
                      <Text>
                        <Text as="span" fontSize={18} color="dark.900">Price per share : </Text>${item.price_per_share}
                      </Text>
                      <Text as="a" href={`/track/${item.track_id}`} _hover={{ color: "primary.900" }}>
                        <Text as="span" fontSize={18} color="dark.900">Artist : </Text>
                        <Text as="span" textDecoration="underline">{item.artist_name}</Text>
                      </Text>
                    </Box>
                  ))}
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      {/* Pagination Controls - Bottom */}
      <Flex 
        justify="space-between" 
        align="center" 
        mt={{ base: 4, md: 6 }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
      >
        <HStack>
          <Button
            size="sm"
            onClick={() => handlePageChange(pagination.current_page - 1)}
            isDisabled={!pagination.has_previous}
            bg="secondary.500"
            color="dark.900"
            _hover={{ bg: "secondary.600" }}
          >
            Previous
          </Button>
          
          <Text fontSize="sm" color="primary.500">
            Page {pagination.current_page} of {pagination.total_pages}
          </Text>
          
          <Button
            size="sm"
            onClick={() => handlePageChange(pagination.current_page + 1)}
            isDisabled={!pagination.has_next}
            bg="secondary.500"
            color="dark.900"
            _hover={{ bg: "secondary.600" }}
          >
            Next
          </Button>
        </HStack>
        
        <Text fontSize="sm" color="primary.500">
          Total: {pagination.total_items} transactions
        </Text>
      </Flex>
    </Box>
  );
};

export default Transaction;
